import { InputAdornment, TextField } from "@mui/material";
import { CalendarBlankIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useState } from "react";
import { useIntl } from "react-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { ResetButton } from "@/components/ResetButton/ResetButton";

import { DatesSelection, type DatesSelectionProps } from "./DatesSelection";

type SingleValue = Date | null;

type RangeValue = {
  from: SingleValue;
  to: SingleValue;
};

export type DatePickerValue<IsRange extends boolean> = IsRange extends true
  ? RangeValue
  : SingleValue;

type DatePickerProps<IsRange extends boolean> = {
  isRange?: IsRange;
  value: DatePickerValue<IsRange>;
  onChange: (value: DatePickerValue<IsRange>) => void;
  onIsRangeChange?: (isRange: boolean) => void;
  onReset?: () => void;
  label: string;
  onFieldClick?: () => void;
  compact?: boolean;
  calendarInitialToValue?: Date | null;
  calendarFromLabel?: string;
  calendarToLabel?: string;
} & Pick<DatesSelectionProps, "renderRangePicker" | "calendarProps">;

export function DatePicker<IsRange extends boolean = false>({
  isRange = false as IsRange,
  value,
  onChange,
  onIsRangeChange,
  onReset,
  label,
  onFieldClick,
  renderRangePicker,
  calendarProps,
  compact = false,
  calendarInitialToValue,
  calendarFromLabel,
  calendarToLabel,
}: DatePickerProps<IsRange>) {
  const intl = useIntl();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  let formattedValue: string;
  if (isRange) {
    const from = formatDate((value as RangeValue).from, compact);
    const to = formatDate((value as RangeValue).to, compact);
    formattedValue = `${from} - ${to}`;
  } else {
    formattedValue = formatDate(value as SingleValue, compact);
  }

  return (
    <>
      <TextField
        name="dates"
        onClick={() => {
          onFieldClick?.();

          setIsDrawerOpen(true);
        }}
        fullWidth
        label={label}
        slotProps={{
          input: {
            startAdornment: !compact && (
              <InputAdornment position="start">
                <CalendarBlankIcon />
              </InputAdornment>
            ),
            endAdornment: !!onReset && (
              <ResetButton
                aria-label={intl.formatMessage({
                  id: "flights.search.resetDates",
                  defaultMessage: "Reset dates",
                })}
                onClick={(event) => {
                  event.stopPropagation();

                  onReset();
                }}
              />
            ),
          },
        }}
        value={formattedValue}
      />

      <SwipeableDrawer
        hasFullHeight
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        disableRestoreFocus
        heightOffset={24}
        ModalProps={{
          keepMounted: false,
        }}
      >
        <DrawerTopBar
          onCloseClick={() => setIsDrawerOpen(false)}
          title={
            isRange
              ? intl.formatMessage({
                  id: "flights.search.selectDates",
                  defaultMessage: "Select dates",
                })
              : intl.formatMessage({
                  id: "flights.search.selectDate",
                  defaultMessage: "Select date",
                })
          }
        />

        <DatesSelection
          initialValues={{
            isRange: isRange ?? false,
            dates: {
              from: isRange
                ? (value as RangeValue).from
                : (value as SingleValue),
              to: isRange
                ? (value as RangeValue).to
                : (calendarInitialToValue ?? null),
            },
          }}
          onConfirm={(values) => {
            onIsRangeChange?.(values.isRange);

            if (values.isRange) {
              onChange({
                from: values.dates.from,
                to: values.dates.to,
              } as DatePickerValue<IsRange>);
            } else {
              onChange(values.dates.from as DatePickerValue<IsRange>);
            }

            setIsDrawerOpen(false);
          }}
          renderRangePicker={renderRangePicker}
          calendarProps={calendarProps}
          fromLabel={calendarFromLabel}
          toLabel={calendarToLabel}
        />
      </SwipeableDrawer>
    </>
  );
}

const formatDate = (date: Date | null, compact: boolean) => {
  if (!date) return "_";

  if (compact) return dayjs(date).format("D MMM");

  return dayjs(date).format("ddd, D MMM");
};
